import React, { useEffect, useState, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Column } from 'simple-flexbox';
import { Box, Typography, Chip, CircularProgress, Alert } from '@mui/material';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Tooltip,
    XAxis,
    YAxis,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Line,
    Area,
    AreaChart
} from 'recharts';
import { useSelector } from 'react-redux';
import { selectUser } from '../../features/userSlice';
import axios from '../../axios';

const useStyles = createUseStyles({
    container: {
        backgroundColor: '#ffffff',
        minHeight: '100vh',
        padding: '0'
    },
    headerSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '-24px',
        marginBottom: '32px',
        // backgroundColor: '#F9FAFB',
        background: 'linear-gradient(135deg, #085d40 0%, #047857 100%)',
        borderRadius: '12px',
        padding: '24px 32px',
        boxShadow: '0 8px 8px rgba(0, 0, 0, 0.15)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '150px',
            height: '150px',
            background:
                'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            borderRadius: '50%',
            transform: 'translate(50%, -50%)'
        }
    },
    pageTitle: {
        fontSize: '28px',
        fontWeight: 700,
        color: 'white',
        marginBottom: '8px'
    },
    pageSubtitle: {
        fontSize: '16px',
        color: '#d1d1d1ff',
        margin: '4px 0 0 0',
        fontWeight: 500
    },
    metricsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '24px',
        marginBottom: '32px',
        '@media (max-width: 1200px)': {
            gridTemplateColumns: 'repeat(2, 1fr)',
            gridTemplateRows: 'repeat(2, 1fr)',
            gap: '20px'
        },
        '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr',
            gridTemplateRows: 'auto',
            gap: '16px'
        }
    },
    metricCard: {
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
        }
    },
    metricCardRevenue: {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
        borderColor: '#bbf7d0',
        '&:hover': {
            boxShadow: '0 8px 30px rgba(34, 197, 94, 0.15)'
        }
    },
    metricCardTransactions: {
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        borderColor: '#bfdbfe',
        '&:hover': {
            boxShadow: '0 8px 30px rgba(59, 130, 246, 0.15)'
        }
    },
    metricCardUsers: {
        background: 'linear-gradient(135deg, #fef7f7 0%, #fef2f2 100%)',
        borderColor: '#fecaca',
        '&:hover': {
            boxShadow: '0 8px 30px rgba(220, 38, 38, 0.15)'
        }
    },
    metricCardPackages: {
        background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)',
        borderColor: '#fed7aa',
        '&:hover': {
            boxShadow: '0 8px 30px rgba(245, 158, 11, 0.15)'
        }
    },
    chartContainer: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        marginBottom: '24px'
    },
    chartTitle: {
        fontSize: '20px',
        fontWeight: 600,
        color: '#1f2937',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    chartsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
        gap: '24px',
        '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr'
        }
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px',
        flexDirection: 'column',
        gap: '16px'
    },
    errorContainer: {
        margin: '20px 0'
    }
});

function Analytics() {
    const classes = useStyles();
    const user = useSelector(selectUser);
    const [barData, setBarData] = useState([]);
    const [metrics, setMetrics] = useState({
        totalRevenue: 0,
        totalTransactions: 0,
        totalUsers: 0,
        totalPackages: 0,
        revenueGrowth: 0,
        transactionGrowth: 0
    });
    const [pieData, setPieData] = useState([]);
    const [trendData, setTrendData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAnalyticsData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch monthly transactions data first (this is the main chart data)
            let monthlyData = [];
            try {
                const transactionsMonthlyResponse = await axios.post('/transactions/monthly_data');
                monthlyData = transactionsMonthlyResponse.data.data || [];
                setBarData(monthlyData);
            } catch (monthlyError) {
                console.warn('Monthly transactions data not available:', monthlyError);
                setBarData([]);
            }

            // Initialize metrics with defaults
            let calculatedMetrics = {
                totalRevenue: 0,
                totalTransactions: 0,
                totalUsers: 0,
                totalPackages: 0,
                revenueGrowth: 0,
                transactionGrowth: 0
            };

            // Fetch additional data with individual error handling
            let allTransactions = [];
            let allPackages = [];
            let allProducts = [];
            let allFarmers = [];

            // Fetch transactions (role-aware for consistency)
            try {
                const transactionsResponse = await axios.get('/transactions');
                allTransactions = transactionsResponse.data.data || [];

                // Filter transactions by user role if not admin
                if (user.role !== 'admin') {
                    // For agents, only include transactions related to their farmers
                    const agentFarmerIds = allFarmers.map((farmer) => farmer._id);
                    allTransactions = allTransactions.filter(
                        (transaction) =>
                            agentFarmerIds.includes(transaction.farmerId) ||
                            agentFarmerIds.includes(transaction.farmer_id)
                    );
                }
            } catch (transError) {
                console.warn('Transactions data not available:', transError);
            }

            // Fetch packages (role-aware for consistency)
            try {
                const packagesResponse = await axios.get('/packages');
                allPackages = packagesResponse.data.data || [];

                // Filter packages by user role if not admin
                if (user.role !== 'admin') {
                    // For agents, only include packages related to their farmers
                    const agentFarmerIds = allFarmers.map((farmer) => farmer._id);
                    allPackages = allPackages.filter(
                        (pkg) =>
                            agentFarmerIds.includes(pkg.farmerId) ||
                            agentFarmerIds.includes(pkg.farmer_id) ||
                            agentFarmerIds.includes(pkg.adderID)
                    );
                }
            } catch (packError) {
                console.warn('Packages data not available:', packError);
            }

            // Fetch products
            try {
                const productsResponse = await axios.get('/products');
                allProducts = productsResponse.data.data || [];
            } catch (prodError) {
                console.warn('Products data not available:', prodError);
            }

            // Fetch farmers (using same logic as dashboard)
            try {
                // Use same endpoint logic as ListOfFarmers component
                let farmersEndpoint = `/farmer/agent/${user.id}`;
                if (user.role === 'admin') {
                    farmersEndpoint = `/farmer`;
                }

                const farmersResponse = await axios.get(farmersEndpoint);
                allFarmers = farmersResponse.data.data || [];
            } catch (farmError) {
                console.warn('Farmers data not available:', farmError);
            }

            // Calculate metrics with available data
            if (allTransactions.length > 0) {
                // Calculate total revenue from transactions
                const totalRevenue = allTransactions.reduce((total, transaction) => {
                    return total + (parseFloat(transaction.amount) || 0);
                }, 0);

                // Calculate growth rates (compare last 30 days vs previous 30 days)
                const now = new Date();
                const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

                const recentTransactions = allTransactions.filter(
                    (t) => new Date(t.createdAt) >= thirtyDaysAgo
                );
                const previousTransactions = allTransactions.filter((t) => {
                    const date = new Date(t.createdAt);
                    return date >= sixtyDaysAgo && date < thirtyDaysAgo;
                });

                const recentRevenue = recentTransactions.reduce(
                    (total, t) => total + (parseFloat(t.amount) || 0),
                    0
                );
                const previousRevenue = previousTransactions.reduce(
                    (total, t) => total + (parseFloat(t.amount) || 0),
                    0
                );

                const revenueGrowth =
                    previousRevenue > 0
                        ? ((recentRevenue - previousRevenue) / previousRevenue) * 100
                        : recentRevenue > 0
                        ? 100
                        : 0;

                const transactionGrowth =
                    previousTransactions.length > 0
                        ? ((recentTransactions.length - previousTransactions.length) /
                              previousTransactions.length) *
                          100
                        : recentTransactions.length > 0
                        ? 100
                        : 0;

                calculatedMetrics.totalRevenue = totalRevenue;
                calculatedMetrics.totalTransactions = allTransactions.length;
                calculatedMetrics.revenueGrowth = Math.round(revenueGrowth * 100) / 100;
                calculatedMetrics.transactionGrowth = Math.round(transactionGrowth * 100) / 100;
            }

            // Set other metrics
            calculatedMetrics.totalUsers = allFarmers.length;
            calculatedMetrics.totalPackages = allPackages.length;

            setMetrics(calculatedMetrics);

            // Create product distribution data from real products or use fallback
            let pieChartData = [];
            if (allProducts.length > 0) {
                // Function to categorize products into better groups
                const categorizeProduct = (product) => {
                    const name = (product.name || '').toLowerCase();
                    const category = (product.category || '').toLowerCase();
                    const description = (product.description || '').toLowerCase();
                    const searchText = `${name} ${category} ${description}`;

                    // Seeds & Planting Materials
                    if (
                        searchText.includes('seed') ||
                        searchText.includes('seedling') ||
                        searchText.includes('plant') ||
                        searchText.includes('maize') ||
                        searchText.includes('bean') ||
                        searchText.includes('sorghum') ||
                        searchText.includes('millet') ||
                        searchText.includes('groundnut') ||
                        searchText.includes('cassava') ||
                        searchText.includes('sweet potato') ||
                        category.includes('seed')
                    ) {
                        return 'Seeds & Planting';
                    }

                    // Fertilizers & Nutrients
                    if (
                        searchText.includes('fertilizer') ||
                        searchText.includes('urea') ||
                        searchText.includes('dap') ||
                        searchText.includes('npk') ||
                        searchText.includes('compost') ||
                        searchText.includes('manure') ||
                        searchText.includes('nutrient') ||
                        category.includes('fertilizer') ||
                        category.includes('nutrient')
                    ) {
                        return 'Fertilizers & Nutrients';
                    }

                    // Pesticides & Chemicals
                    if (
                        searchText.includes('pesticide') ||
                        searchText.includes('insecticide') ||
                        searchText.includes('herbicide') ||
                        searchText.includes('fungicide') ||
                        searchText.includes('chemical') ||
                        searchText.includes('spray') ||
                        category.includes('pesticide') ||
                        category.includes('chemical')
                    ) {
                        return 'Pesticides & Chemicals';
                    }

                    // Farm Tools & Equipment
                    if (
                        searchText.includes('tool') ||
                        searchText.includes('hoe') ||
                        searchText.includes('machete') ||
                        searchText.includes('spade') ||
                        searchText.includes('rake') ||
                        searchText.includes('equipment') ||
                        searchText.includes('machinery') ||
                        searchText.includes('tractor') ||
                        searchText.includes('plow') ||
                        category.includes('tool') ||
                        category.includes('equipment')
                    ) {
                        return 'Tools & Equipment';
                    }

                    // Irrigation & Water Management
                    if (
                        searchText.includes('irrigation') ||
                        searchText.includes('water') ||
                        searchText.includes('pipe') ||
                        searchText.includes('sprinkler') ||
                        searchText.includes('pump') ||
                        searchText.includes('tank') ||
                        category.includes('irrigation') ||
                        category.includes('water')
                    ) {
                        return 'Irrigation & Water';
                    }

                    // Post-Harvest & Storage
                    if (
                        searchText.includes('storage') ||
                        searchText.includes('silo') ||
                        searchText.includes('bag') ||
                        searchText.includes('container') ||
                        searchText.includes('warehouse') ||
                        searchText.includes('drying') ||
                        category.includes('storage') ||
                        category.includes('post-harvest')
                    ) {
                        return 'Storage & Processing';
                    }

                    // Livestock & Animal Feeds
                    if (
                        searchText.includes('feed') ||
                        searchText.includes('livestock') ||
                        searchText.includes('cattle') ||
                        searchText.includes('chicken') ||
                        searchText.includes('pig') ||
                        searchText.includes('animal') ||
                        searchText.includes('poultry') ||
                        category.includes('livestock') ||
                        category.includes('animal')
                    ) {
                        return 'Livestock & Feeds';
                    }

                    // If product has a specific category, use it
                    if (
                        category &&
                        category !== 'other' &&
                        category !== 'others' &&
                        category.length > 0
                    ) {
                        // Capitalize first letter of each word
                        return category
                            .split(' ')
                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ');
                    }

                    // Default category
                    return 'Other Products';
                };

                const productCategories = {};
                allProducts.forEach((product) => {
                    const category = categorizeProduct(product);
                    if (!productCategories[category]) {
                        productCategories[category] = 0;
                    }
                    productCategories[category] += 1;
                });

                // Enhanced color palette for better category distinction
                const colors = [
                    '#059669', // Green for Seeds & Planting
                    '#3b82f6', // Blue for Fertilizers & Nutrients
                    '#dc2626', // Red for Pesticides & Chemicals
                    '#f59e0b', // Orange for Tools & Equipment
                    '#06b6d4', // Cyan for Irrigation & Water
                    '#8b5cf6', // Purple for Storage & Processing
                    '#84cc16', // Lime for Livestock & Feeds
                    '#f97316', // Dark Orange
                    '#ef4444', // Light Red
                    '#10b981', // Emerald
                    '#6366f1', // Indigo
                    '#ec4899' // Pink
                ];

                // Sort categories by count (largest first) for better visual hierarchy
                const sortedCategories = Object.entries(productCategories).sort(
                    ([, a], [, b]) => b - a
                );

                pieChartData = sortedCategories.map(([category, count], index) => ({
                    name: category,
                    value: Math.round((count / allProducts.length) * 100),
                    count: count, // Add actual count for tooltip
                    color: colors[index % colors.length]
                }));
            } else {
                // Fallback data if no products available
                pieChartData = [{ name: 'No Data Available', value: 100, color: '#9ca3af' }];
            }

            setPieData(pieChartData);

            // Create trend data from monthly data with real revenue calculations
            let trendData = [];
            if (monthlyData.length > 0 && allTransactions.length > 0) {
                const avgTransactionAmount =
                    calculatedMetrics.totalRevenue / calculatedMetrics.totalTransactions;

                trendData = monthlyData.map((yearData) => {
                    // Calculate total transactions for the year
                    const yearTransactions = Object.values(yearData)
                        .slice(1) // Skip 'year' field
                        .reduce((a, b) => (parseFloat(a) || 0) + (parseFloat(b) || 0), 0);

                    // Calculate revenue based on average transaction amount
                    const estimatedRevenue = Math.floor(yearTransactions * avgTransactionAmount);

                    return {
                        year: yearData.year || 'Unknown',
                        revenue: estimatedRevenue || 0,
                        transactions: yearTransactions || 0
                    };
                });
            } else {
                // Fallback trend data
                trendData = [
                    { year: '2023', revenue: 0, transactions: 0 },
                    { year: '2024', revenue: 0, transactions: 0 }
                ];
            }

            setTrendData(trendData);
        } catch (err) {
            setError('Failed to fetch analytics data');
            console.error('Analytics fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [user.id, user.role]);

    useEffect(() => {
        fetchAnalyticsData();
    }, [fetchAnalyticsData]);

    return (
        <div className={classes.container}>
            <Column>
                {/* Header Section */}
                <div className={classes.headerSection}>
                    <div>
                        <h1 className={classes.pageTitle}>Analytics Dashboard</h1>
                        <p className={classes.pageSubtitle}>
                            Comprehensive insights into your agricultural business performance
                        </p>
                    </div>
                    <Chip
                        label={`Last Updated: ${new Date().toLocaleDateString()}`}
                        sx={{
                            backgroundColor: '#085d40',
                            color: '#ffffff',
                            fontWeight: 600
                        }}
                    />
                </div>

                {/* Loading State */}
                {loading && (
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={60} sx={{ color: '#085d40' }} />
                        <Typography variant='h6' color='textSecondary'>
                            Loading analytics data...
                        </Typography>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className={classes.errorContainer}>
                        <Alert severity='error' sx={{ borderRadius: '12px' }}>
                            <Typography variant='h6' gutterBottom>
                                Error Loading Data
                            </Typography>
                            <Typography variant='body2'>
                                {error}. Please try refreshing the page.
                            </Typography>
                        </Alert>
                    </div>
                )}

                {/* Main Content */}
                {!loading && !error && (
                    <>
                        {/* Metrics Cards */}
                        <div className={classes.metricsGrid}>
                            {/* Revenue Card */}
                            <div className={`${classes.metricCard} ${classes.metricCardRevenue}`}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <div>
                                        <Typography
                                            variant='h6'
                                            sx={{
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Total Revenue
                                        </Typography>
                                    </div>
                                    <Chip
                                        label={`+${metrics.revenueGrowth}%`}
                                        size='small'
                                        sx={{
                                            backgroundColor: '#dcfce7',
                                            color: '#059669',
                                            fontSize: '10px',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                                <Typography
                                    variant='h3'
                                    sx={{
                                        color: '#059669',
                                        fontSize: '32px',
                                        fontWeight: 700,
                                        margin: 0,
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {metrics.totalRevenue.toLocaleString('en-US')}
                                </Typography>
                                <Typography
                                    variant='body2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '13px',
                                        marginTop: '4px',
                                        fontWeight: 500
                                    }}
                                >
                                    UGX Total Earnings
                                </Typography>
                            </div>

                            {/* Transactions Card */}
                            <div
                                className={`${classes.metricCard} ${classes.metricCardTransactions}`}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <div>
                                        <Typography
                                            variant='h6'
                                            sx={{
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Total Transactions
                                        </Typography>
                                    </div>
                                    <Chip
                                        label={`+${metrics.transactionGrowth}%`}
                                        size='small'
                                        sx={{
                                            backgroundColor: '#dbeafe',
                                            color: '#3b82f6',
                                            fontSize: '10px',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                                <Typography
                                    variant='h3'
                                    sx={{
                                        color: '#3b82f6',
                                        fontSize: '32px',
                                        fontWeight: 700,
                                        margin: 0,
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {metrics.totalTransactions.toLocaleString('en-US')}
                                </Typography>
                                <Typography
                                    variant='body2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '13px',
                                        marginTop: '4px',
                                        fontWeight: 500
                                    }}
                                >
                                    Completed Transactions
                                </Typography>
                            </div>

                            {/* Users Card */}
                            <div className={`${classes.metricCard} ${classes.metricCardUsers}`}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <div>
                                        <Typography
                                            variant='h6'
                                            sx={{
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Active Farmers
                                        </Typography>
                                    </div>
                                    <Chip
                                        label='Active'
                                        size='small'
                                        sx={{
                                            backgroundColor: '#fee2e2',
                                            color: '#dc2626',
                                            fontSize: '10px',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                                <Typography
                                    variant='h3'
                                    sx={{
                                        color: '#dc2626',
                                        fontSize: '32px',
                                        fontWeight: 700,
                                        margin: 0,
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {metrics.totalUsers.toLocaleString('en-US')}
                                </Typography>
                                <Typography
                                    variant='body2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '13px',
                                        marginTop: '4px',
                                        fontWeight: 500
                                    }}
                                >
                                    Registered Farmers
                                </Typography>
                            </div>

                            {/* Packages Card */}
                            <div className={`${classes.metricCard} ${classes.metricCardPackages}`}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '16px'
                                    }}
                                >
                                    <div>
                                        <Typography
                                            variant='h6'
                                            sx={{
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Total Packages
                                        </Typography>
                                    </div>
                                    <Chip
                                        label='Delivered'
                                        size='small'
                                        sx={{
                                            backgroundColor: '#fef3c7',
                                            color: '#f59e0b',
                                            fontSize: '10px',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                                <Typography
                                    variant='h3'
                                    sx={{
                                        color: '#f59e0b',
                                        fontSize: '32px',
                                        fontWeight: 700,
                                        margin: 0,
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {metrics.totalPackages.toLocaleString('en-US')}
                                </Typography>
                                <Typography
                                    variant='body2'
                                    sx={{
                                        color: '#6b7280',
                                        fontSize: '13px',
                                        marginTop: '4px',
                                        fontWeight: 500
                                    }}
                                >
                                    Packages Distributed
                                </Typography>
                            </div>
                        </div>

                        {/* Charts Section */}
                        <div className={classes.chartsGrid}>
                            {/* Monthly Transactions Chart */}
                            <div className={classes.chartContainer}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '24px',
                                        paddingBottom: '16px',
                                        borderBottom: '2px solid #f1f5f9'
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant='h5'
                                            sx={{
                                                color: '#1f2937',
                                                fontSize: '22px',
                                                fontWeight: 700,
                                                marginBottom: '4px'
                                            }}
                                        >
                                            Monthly Transactions Overview
                                        </Typography>
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                color: '#6b7280',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }}
                                        >
                                            Track your monthly transaction patterns across all years
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <Chip
                                            label={`${barData.length} Years of Data`}
                                            size='small'
                                            sx={{
                                                backgroundColor: '#dcfce7',
                                                color: '#059669',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                border: '1px solid #bbf7d0'
                                            }}
                                        />
                                        <Chip
                                            label='Live Data'
                                            size='small'
                                            sx={{
                                                backgroundColor: '#fef3c7',
                                                color: '#f59e0b',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                border: '1px solid #fed7aa'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <ResponsiveContainer width='100%' height={400}>
                                    <BarChart
                                        data={barData}
                                        margin={{ top: 30, right: 40, left: 20, bottom: 20 }}
                                        barCategoryGap='10%'
                                    >
                                        <defs>
                                            {[
                                                { id: 'jan', color1: '#059669', color2: '#10b981' },
                                                { id: 'feb', color1: '#3b82f6', color2: '#60a5fa' },
                                                { id: 'mar', color1: '#dc2626', color2: '#f87171' },
                                                { id: 'apr', color1: '#f59e0b', color2: '#fbbf24' },
                                                { id: 'may', color1: '#8b5cf6', color2: '#a78bfa' },
                                                { id: 'jun', color1: '#06b6d4', color2: '#22d3ee' },
                                                { id: 'jul', color1: '#84cc16', color2: '#a3e635' },
                                                { id: 'aug', color1: '#f97316', color2: '#fb923c' },
                                                { id: 'sep', color1: '#ef4444', color2: '#f87171' },
                                                { id: 'oct', color1: '#6366f1', color2: '#8b5cf6' },
                                                { id: 'nov', color1: '#ec4899', color2: '#f472b6' },
                                                { id: 'dec', color1: '#14b8a6', color2: '#2dd4bf' }
                                            ].map(({ id, color1, color2 }) => (
                                                <linearGradient
                                                    key={id}
                                                    id={`gradient-${id}`}
                                                    x1='0'
                                                    y1='0'
                                                    x2='0'
                                                    y2='1'
                                                >
                                                    <stop offset='0%' stopColor={color1} />
                                                    <stop
                                                        offset='100%'
                                                        stopColor={color2}
                                                        stopOpacity={0.8}
                                                    />
                                                </linearGradient>
                                            ))}
                                        </defs>
                                        <CartesianGrid
                                            strokeDasharray='8 8'
                                            stroke='#f1f5f9'
                                            strokeWidth={1}
                                            opacity={0.6}
                                        />
                                        <XAxis
                                            dataKey='year'
                                            tick={{
                                                fontSize: 13,
                                                fill: '#374151',
                                                fontWeight: 600
                                            }}
                                            axisLine={{
                                                stroke: '#e5e7eb',
                                                strokeWidth: 2
                                            }}
                                            tickLine={{
                                                stroke: '#9ca3af',
                                                strokeWidth: 1
                                            }}
                                        />
                                        <YAxis
                                            tick={{
                                                fontSize: 13,
                                                fill: '#374151',
                                                fontWeight: 500
                                            }}
                                            axisLine={{
                                                stroke: '#e5e7eb',
                                                strokeWidth: 2
                                            }}
                                            tickLine={{
                                                stroke: '#9ca3af',
                                                strokeWidth: 1
                                            }}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                border: 'none',
                                                borderRadius: '16px',
                                                boxShadow:
                                                    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                                padding: '16px',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }}
                                            labelStyle={{
                                                color: '#1f2937',
                                                fontWeight: 700,
                                                marginBottom: '8px'
                                            }}
                                            cursor={{
                                                fill: 'rgba(5, 150, 105, 0.05)',
                                                stroke: '#059669',
                                                strokeWidth: 2,
                                                strokeDasharray: '4 4'
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                paddingTop: '20px',
                                                fontSize: '13px',
                                                fontWeight: 600
                                            }}
                                        />
                                        {[
                                            'Jan',
                                            'Feb',
                                            'Mar',
                                            'Apr',
                                            'May',
                                            'Jun',
                                            'Jul',
                                            'Aug',
                                            'Sep',
                                            'Oct',
                                            'Nov',
                                            'Dec'
                                        ].map((month, index) => (
                                            <Bar
                                                key={month}
                                                dataKey={month}
                                                fill={`url(#gradient-${month.toLowerCase()})`}
                                                radius={[6, 6, 0, 0]}
                                                stroke='rgba(255, 255, 255, 0.8)'
                                                strokeWidth={1}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Product Distribution Pie Chart */}
                            <div className={classes.chartContainer}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '24px',
                                        paddingBottom: '16px',
                                        borderBottom: '2px solid #f1f5f9'
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant='h5'
                                            sx={{
                                                color: '#1f2937',
                                                fontSize: '22px',
                                                fontWeight: 700,
                                                marginBottom: '4px'
                                            }}
                                        >
                                            Product Distribution
                                        </Typography>
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                color: '#6b7280',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }}
                                        >
                                            Breakdown of products sold by category
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <Chip
                                            label='By Category'
                                            size='small'
                                            sx={{
                                                backgroundColor: '#dbeafe',
                                                color: '#3b82f6',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                border: '1px solid #bfdbfe'
                                            }}
                                        />
                                        <Chip
                                            label='Active Sales'
                                            size='small'
                                            sx={{
                                                backgroundColor: '#dcfce7',
                                                color: '#059669',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                border: '1px solid #bbf7d0'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                                    {/* Chart Section */}
                                    <Box sx={{ flex: 1 }}>
                                        <ResponsiveContainer width='100%' height={350}>
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx='50%'
                                                    cy='50%'
                                                    outerRadius={100}
                                                    innerRadius={40}
                                                    paddingAngle={3}
                                                    dataKey='value'
                                                    stroke='#ffffff'
                                                    strokeWidth={3}
                                                >
                                                    {pieData.map((entry, index) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={entry.color}
                                                            style={{
                                                                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))',
                                                                cursor: 'pointer'
                                                            }}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#ffffff',
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                        boxShadow:
                                                            '0 10px 25px rgba(0, 0, 0, 0.15)',
                                                        padding: '16px',
                                                        fontSize: '14px',
                                                        fontWeight: 500
                                                    }}
                                                    formatter={(value, name, props) => [
                                                        `${value}% (${props.payload.count} products)`,
                                                        name
                                                    ]}
                                                    labelStyle={{
                                                        color: '#1f2937',
                                                        fontWeight: 700,
                                                        marginBottom: '4px'
                                                    }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>

                                    {/* Legend Section */}
                                    <Box sx={{ minWidth: '200px' }}>
                                        <Typography
                                            variant='h6'
                                            sx={{
                                                color: '#374151',
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                marginBottom: '16px'
                                            }}
                                        >
                                            Categories
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '12px'
                                            }}
                                        >
                                            {pieData.map((entry, index) => (
                                                <Box
                                                    key={entry.name}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        padding: '8px 12px',
                                                        borderRadius: '8px',
                                                        backgroundColor: '#f9fafb',
                                                        border: '1px solid #f3f4f6',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: '#f3f4f6',
                                                            transform: 'translateX(4px)',
                                                            boxShadow:
                                                                '0 2px 8px rgba(0, 0, 0, 0.1)'
                                                        }
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '16px',
                                                            height: '16px',
                                                            borderRadius: '4px',
                                                            backgroundColor: entry.color,
                                                            boxShadow:
                                                                '0 2px 4px rgba(0, 0, 0, 0.1)'
                                                        }}
                                                    />
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                color: '#374151',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                                lineHeight: 1.2
                                                            }}
                                                        >
                                                            {entry.name}
                                                        </Typography>
                                                        <Typography
                                                            variant='caption'
                                                            sx={{
                                                                color: '#6b7280',
                                                                fontSize: '12px',
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            {entry.value}% ({entry.count} products)
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </div>

                            {/* Revenue Trend Line Chart */}
                            <div
                                className={classes.chartContainer}
                                style={{ gridColumn: '1 / -1' }}
                            >
                                <div className={classes.chartTitle}>
                                    Revenue & Transaction Trends
                                    <Chip
                                        label='Year over Year'
                                        size='small'
                                        sx={{
                                            backgroundColor: '#f3f4f6',
                                            color: '#374151',
                                            fontSize: '11px'
                                        }}
                                    />
                                </div>
                                <ResponsiveContainer width='100%' height={400}>
                                    <AreaChart
                                        data={trendData}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9' />
                                        <XAxis
                                            dataKey='year'
                                            tick={{ fontSize: 12, fill: '#6b7280' }}
                                            axisLine={{ stroke: '#e2e8f0' }}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 12, fill: '#6b7280' }}
                                            axisLine={{ stroke: '#e2e8f0' }}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                            }}
                                        />
                                        <Legend />
                                        <Area
                                            type='monotone'
                                            dataKey='revenue'
                                            stroke='#059669'
                                            fill='#059669'
                                            fillOpacity={0.3}
                                            strokeWidth={3}
                                        />
                                        <Line
                                            type='monotone'
                                            dataKey='transactions'
                                            stroke='#3b82f6'
                                            strokeWidth={3}
                                            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </>
                )}
            </Column>
        </div>
    );
}

export default Analytics;
